import React, { useEffect, useMemo, useState } from 'react';

const ageGroup = (ageStr) => {
    const n = Number(String(ageStr || '').replace(/[^0-9]/g, ''));
    if (!Number.isFinite(n)) return 'Unknown';
    if (n <= 12) return 'Child (0–12)';
    if (n <= 17) return 'Youth (13–17)';
    if (n <= 23) return 'Young Adult (18–23)';
    return 'Adult (24+)';
};

export default function ChairmanRegistrations({ token }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [printRow, setPrintRow] = useState(null);

    const fetchRows = async () => {
        setError('');
        setLoading(true);
        try {
            const res = await fetch('/.netlify/functions/admin-get-chairman-registrations', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const raw = await res.text();
            let json = {};
            try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }
            if (!res.ok) {
                setError(json?.error || `Failed to load: ${res.status}`);
                setRows([]);
                return;
            }
            setRows(Array.isArray(json.rows) ? json.rows : []);
        } catch {
            setError('Network error while loading registrations');
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) return;
        fetchRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const groups = useMemo(() => {
        const g = new Map();
        rows.forEach((r) => {
            const key = ageGroup(r.age);
            if (!g.has(key)) g.set(key, []);
            g.get(key).push(r);
        });

        // stable order
        const order = ['Child (0–12)', 'Youth (13–17)', 'Young Adult (18–23)', 'Adult (24+)', 'Unknown'];
        return order
            .filter((k) => g.has(k))
            .map((k) => ({ label: k, items: g.get(k) }));
    }, [rows]);

    const needs = (r) => String(r?.needsAssistance || '').toLowerCase() === 'yes';

    const openPrint = (r) => {
        setPrintRow(r);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => window.print());
        });
    };

    return (
        <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
            {/* Print area (only visible in print via your existing Admin.css rules) */}
            {printRow ? (
                <div className="print-area" style={{ position: 'absolute', left: '-10000px', top: 0 }}>
                    <h1>Sponsorship Request</h1>
                    <p><strong>Name:</strong> {printRow.name || `${printRow.first} ${printRow.last}`}</p>
                    <p><strong>DOB:</strong> {printRow.dob || '—'}</p>
                    <p><strong>Payment method:</strong> {printRow.payment || '—'}</p>

                    <h2>Sponsorship Questions</h2>
                    <p><strong>Sponsorship requested:</strong> {printRow.needsAssistance || 'No'}</p>

                    {String(printRow?.sponsorship?.reason || '').trim() ? (
                        <p><strong>Reason:</strong> {printRow.sponsorship.reason}</p>
                    ) : null}

                    {String(printRow?.sponsorship?.fundraisingEfforts || '').trim() ? (
                        <p><strong>Fundraising efforts:</strong> {printRow.sponsorship.fundraisingEfforts}</p>
                    ) : null}

                    {String(printRow?.sponsorship?.fundraisingDetails || '').trim() ? (
                        <p><strong>Fundraising details:</strong> {printRow.sponsorship.fundraisingDetails}</p>
                    ) : null}

                    {String(printRow?.sponsorship?.details || '').trim() ? (
                        <p><strong>Sponsorship details (from sheet):</strong> {printRow.sponsorship.details}</p>
                    ) : null}
                </div>
            ) : null}

            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                    <div>
                        <h2 style={{ margin: 0 }}>Registrations</h2>
                        <div className="muted" style={{ marginTop: 4 }}>
                            Registration details
                        </div>
                    </div>

                    <button className="btn" onClick={fetchRows} disabled={loading}>
                        Refresh
                    </button>
                </div>

                {error ? <div className="error" style={{ marginTop: 10 }}>{error}</div> : null}
                {loading ? <div style={{ marginTop: 10 }}>Loading…</div> : null}

                {!loading && !rows.length ? (
                    <div className="muted" style={{ marginTop: 10 }}>No registrations found.</div>
                ) : null}

                {!loading && rows.length ? (
                    <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
                        {groups.map((grp) => (
                            <div key={grp.label} style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                    <div style={{ fontWeight: 900 }}>{grp.label}</div>
                                    <div className="muted" style={{ fontSize: 12 }}>{grp.items.length} total</div>
                                </div>

                                <div className="table-wrap" style={{ marginTop: 10 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                        <thead>
                                            <tr>
                                                {['First', 'Last', 'DOB', 'Needs assistance', ''].map((h) => (
                                                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 12, color: '#666' }}>
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {grp.items.map((r) => (
                                                <tr key={r.id}>
                                                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f1f1', fontWeight: 800 }}>
                                                        {r.first || '—'}
                                                    </td>
                                                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f1f1', fontWeight: 800 }}>
                                                        {r.last || '—'}
                                                    </td>
                                                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f1f1', whiteSpace: 'nowrap' }}>
                                                        {r.dob || '—'}
                                                    </td>
                                                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f1f1' }}>
                                                        {needs(r) ? 'Yes' : 'No'}
                                                    </td>
                                                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f1f1' }}>
                                                        {needs(r) ? (
                                                            <button className="btn" type="button" onClick={() => openPrint(r)} style={{ padding: '6px 10px' }}>
                                                                Print
                                                            </button>
                                                        ) : (
                                                            <span className="muted">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
}